import { DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { JSX } from 'react';
import { useContext, useMemo, useState } from 'react';

import AiProfileShortcuts from '@/components/AiProfileShortcuts';
import BetaGate from '@/components/BetaGate';
import BetaSwitch from '@/components/BetaSwitch';
import CardOrderDraftBar from '@/components/CardOrderDraftBar';
import CompactTaskList from '@/components/CompactTaskList';
import EpisodeOfCareSection from '@/components/EpisodeOfCareSection';
import InsurancePanel from '@/components/InsurancePanel';
import InsuranceSection from '@/components/InsuranceSection';
import MostRecentChallengeResponse from '@/components/MostRecentChallengeResponse';
import PageHeader from '@/components/PageHeader';
import PersonDemographics from '@/components/PersonDemographics';
import PersonMedicationsSummary from '@/components/PersonMedicationsSummary';
import PersonRecentContact from '@/components/PersonRecentContact';
import ScheduleSummary from '@/components/ScheduleSummary';
import { taskable_type_person } from '@/constants/taskableTypes';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import usePageCardOrder from '@/hooks/usePageCardOrder';
import PersonContext from '@/pages/Person/PersonContext';
import AdminSection from '@/pages/Profile/components/AdminSection/AdminSection';
import ChecklistSection from '@/pages/Profile/components/ChecklistSection';
import ClassificationsList from '@/pages/Profile/components/ClassificationsList/ClassificationsList';
import DischargeAndRejections from '@/pages/Profile/components/DischargeAndRejections/DischargeAndRejections';
import EngagementInformationSection from '@/pages/Profile/components/EngagementInformationSection';
import HiddenCardsDivider from '@/pages/Profile/components/HiddenCardsDivider';
import HideCardModal from '@/pages/Profile/components/HideCardModal';
import MemberAvailabilitySection from '@/pages/Profile/components/MemberAvailabilitySection';
import PersonDuplicatesSection from '@/pages/Profile/components/PersonDuplicatesSection';
import SchedulingLinksSection from '@/pages/Profile/components/SchedulingLinksSection';
import SectionHeading from '@/pages/Profile/components/SectionHeading';
import SortableSection from '@/pages/Profile/components/SortableSection/SortableSection';
import { DraggableSection, ProfileSection } from '@/pages/Profile/types';
import hideDisqualificationsAndRejections from '@/pages/Profile/utils/hideDisqualificationsAndRejections';
import AppointmentsRepository, {
  type GetAppointmentsParams
} from '@/repositories/AppointmentsRepository';
import { SeeAllLink } from '@/ui';
import Container from '@/ui/components/Container';
import TagsEditor from '@/ui/components/TagsEditor';
import { hasPermission, PEOPLE_VIEW_AI_PROFILE } from '@/utils/userPermission';

import Banners from './components/Banners';
import BasicInfoSection from './components/BasicInfoSection';
import CareTeamSection from './components/CareTeamSection';
import ClassificationsSection from './components/ClassificationsSection';
import ContactInfoSection from './components/ContactInfoSection';
import OutlinksSection from './components/OutlinksSection';
import Referrals from './components/Referrals';
import TreatabilitySection from './components/TreatabilitySection';

const Profile = (): JSX.Element | null => {
  const { host, permissions } = useContext(AppContext);
  const enableAiProfile = hasPermission({
    permissions,
    requiredPermission: PEOPLE_VIEW_AI_PROFILE
  });
  const {
    person,
    person: {
      external_links: { healthie, homebase, iterable, mixpanel },
      kustomer_id
    }
  } = useContext(PersonContext);

  const apptRepo = AppointmentsRepository();
  const { data: nextAppointmentData } = useAPI({
    queryKey: ['getNextAppointment', person?.id],
    queryFn: () => {
      const params: Partial<GetAppointmentsParams> = {
        order_by: 'appointment__start_at',
        page: 1,
        person_id: person?.id,
        size: 1,
        upcoming: true
      };

      return apptRepo.getAppointments(params);
    }
  });

  const nextAppointment = useMemo(
    () => nextAppointmentData?.data?.results[0],
    [nextAppointmentData]
  );
  const hasOngoingAppointment = useMemo(() => {
    if (nextAppointment) {
      const {
        appointment: { start_at, end_at },
        internal_status
      } = nextAppointment;
      if (start_at && end_at) {
        const now = new Date();
        const startAt = new Date(start_at);
        const endAt = new Date(end_at);
        return (
          !['no_show', 'cancelled'].includes(internal_status) &&
          startAt.getTime() <= now.getTime() &&
          now.getTime() <= endAt.getTime()
        );
      }
    }
    return false;
  }, [nextAppointment]);

  const { episode_of_care, program_qualification } = person;

  const taskables = useMemo(
    () => [{ contentTypeName: taskable_type_person, id: person.id }],
    [person.id]
  );

  const sectionsList: DraggableSection[] = [
    { defaultOpen: true, id: ProfileSection.SEGMENTS, title: 'Segments' },
    { defaultOpen: true, id: ProfileSection.CLASSIFICATIONS, title: 'Classifications' },
    { defaultOpen: true, id: ProfileSection.BASIC_INFO, title: 'Basic Info' },
    {
      defaultOpen: true,
      id: ProfileSection.DISCHARGE_AND_REJECTIONS,
      isUnavailable: hideDisqualificationsAndRejections(program_qualification, episode_of_care),
      title: 'Discharge & Rejections'
    },
    { defaultOpen: true, id: ProfileSection.TREATABILITY, title: 'Treatability' },
    {
      betaFeature: 'profileInsurance',
      defaultOpen: true,
      id: ProfileSection.INSURANCE,
      title: 'Insurance'
    },
    { defaultOpen: true, id: ProfileSection.MEMBER_AVAILABILITY, title: 'Member Availability' },
    { defaultOpen: true, id: ProfileSection.CONTACT_INFO, title: 'Contact Info' },
    {
      defaultOpen: true,
      id: ProfileSection.ENGAGEMENT_INFORMATION,
      title: 'Engagement Information'
    },
    { defaultOpen: true, id: ProfileSection.CARE_TEAM, title: 'Member Care' },
    { defaultOpen: true, id: ProfileSection.TAGS, title: 'Tags' },
    { defaultOpen: true, id: ProfileSection.REFERRALS, title: 'Referrals' },
    { defaultOpen: true, id: ProfileSection.OUTLINKS, title: 'Outlinks' },
    { defaultOpen: true, id: ProfileSection.ADMIN, title: 'Admin' },
    { defaultOpen: true, id: ProfileSection.EPISODE_OF_CARE, title: 'Episode of Care' },
    { defaultOpen: true, id: ProfileSection.SCHEDULING_LINKS, title: 'Scheduling Links' },
    {
      defaultOpen: true,
      id: ProfileSection.PERSON_DUPLICATES,
      isUnavailable: !person.potential_duplicates.length,
      title: 'Potential Duplicates'
    },
    { defaultOpen: true, id: ProfileSection.CHECKLIST, title: 'Checklist' },
    { defaultOpen: true, id: ProfileSection.DEMOGRAPHICS, title: 'Demographics' },
    { defaultOpen: true, hidden: true, id: ProfileSection.TASKS, title: 'Tasks' },
    {
      defaultOpen: true,
      hidden: true,
      id: ProfileSection.CHALLENGE_RESPONSE,
      title: 'Last Challenge Response'
    },
    { defaultOpen: true, hidden: true, id: ProfileSection.MEDICATIONS, title: 'Medications' },
    { defaultOpen: true, hidden: true, id: ProfileSection.RECENT_CONTACT, title: 'Recent Contact' },
    { defaultOpen: true, hidden: true, id: ProfileSection.SCHEDULE, title: 'Next Appointments' },
    {
      defaultOpen: true,
      hidden: true,
      id: ProfileSection.ASK_AI,
      isUnavailable: !enableAiProfile,
      title: 'Ask AI'
    }
  ];

  const sectionActions = (section: DraggableSection) => {
    if (section.betaFeature) return <BetaSwitch feature={section.betaFeature} />;

    switch (section.id) {
      case ProfileSection.MEDICATIONS:
        return <SeeAllLink to={`/people/${person.id}/medications`} />;
      case ProfileSection.CHALLENGE_RESPONSE:
        return <SeeAllLink to={`/people/${person.id}/challenges`} />;
      case ProfileSection.RECENT_CONTACT:
        return <SeeAllLink to={`/people/${person.id}/messaging`} />;
      case ProfileSection.SCHEDULE:
        return <SeeAllLink label="Show appointments" to={`/people/${person.id}/appointments`} />;
      case ProfileSection.TASKS:
        return <SeeAllLink label="View all tasks" to={`/people/${person.id}/tasks`} />;
      default:
        return undefined;
    }
  };

  const {
    cards: sections,
    draftBar,
    onDragEnd: handleSectionDragEnd,
    onHide: hideSection,
    onToggle: handleSectionToggle,
    onUnhide: unhideSection
  } = usePageCardOrder<DraggableSection>({
    cards: sectionsList,
    settingKey: 'profileSections'
  });

  const [sectionToHide, setSectionToHide] = useState<DraggableSection | null>(null);
  const [showHiddenSections, setShowHiddenSections] = useState(false);

  const availableSections = sections.filter(({ isUnavailable }) => !isUnavailable);
  const shownSections = availableSections.filter(({ hidden }) => !hidden);
  const hiddenSections = availableSections.filter(({ hidden }) => hidden);

  const confirmHideSection = () => {
    if (sectionToHide) hideSection(sectionToHide.id);
    setSectionToHide(null);
  };

  const sectionComponent = (sectionId: string): JSX.Element | null => {
    switch (sectionId) {
      case ProfileSection.ADMIN:
        return <AdminSection />;

      case ProfileSection.ASK_AI:
        return (
          <div className="mt-2 flex flex-wrap gap-1">
            <AiProfileShortcuts personId={person.id} />
          </div>
        );

      case ProfileSection.BASIC_INFO:
        return <BasicInfoSection />;

      case ProfileSection.CHALLENGE_RESPONSE:
        return <MostRecentChallengeResponse personId={person.id} />;

      case ProfileSection.MEDICATIONS:
        return <PersonMedicationsSummary />;

      case ProfileSection.RECENT_CONTACT:
        return <PersonRecentContact />;

      case ProfileSection.SCHEDULE:
        return <ScheduleSummary />;

      case ProfileSection.TASKS:
        return <CompactTaskList personId={person.id} taskables={taskables} />;

      case ProfileSection.DEMOGRAPHICS:
        return <PersonDemographics />;

      case ProfileSection.CARE_TEAM:
        return <CareTeamSection />;

      case ProfileSection.CONTACT_INFO:
        return <ContactInfoSection />;

      case ProfileSection.CLASSIFICATIONS:
        return <ClassificationsSection />;

      case ProfileSection.DISCHARGE_AND_REJECTIONS:
        return <DischargeAndRejections person={person} />;

      case ProfileSection.ENGAGEMENT_INFORMATION:
        return (
          <EngagementInformationSection
            nextAppointment={nextAppointment}
            hasOngoingAppointment={hasOngoingAppointment}
          />
        );

      case ProfileSection.EPISODE_OF_CARE:
        return <EpisodeOfCareSection showTitle={false} />;

      case ProfileSection.INSURANCE:
        return (
          <BetaGate feature="profileInsurance" fallback={<InsuranceSection />}>
            <InsurancePanel />
          </BetaGate>
        );

      case ProfileSection.MEMBER_AVAILABILITY:
        return <MemberAvailabilitySection />;

      case ProfileSection.OUTLINKS:
        return (
          <OutlinksSection
            healthieUrl={healthie?.url}
            host={host}
            homebaseUrl={homebase.url}
            iterableUrl={iterable?.url}
            mixpanelUrl={mixpanel?.url}
            kustomerId={kustomer_id}
            person={person}
            userId={person.user?.id}
          />
        );

      case ProfileSection.REFERRALS:
        return (
          <div>
            <SectionHeading text="Referrals" />
            <Referrals />
          </div>
        );

      case ProfileSection.SCHEDULING_LINKS:
        return <SchedulingLinksSection />;

      case ProfileSection.SEGMENTS:
        return <ClassificationsList classifications={person.classifications} />;

      case ProfileSection.TAGS:
        return (
          <div>
            <SectionHeading text="Tags" />
            <TagsEditor contentType="people" />
          </div>
        );

      case ProfileSection.TREATABILITY:
        return <TreatabilitySection />;

      case ProfileSection.PERSON_DUPLICATES:
        return <PersonDuplicatesSection />;

      case ProfileSection.CHECKLIST:
        return <ChecklistSection />;

      default:
        return null;
    }
  };

  if (person) {
    return (
      <Container type="page">
        <PageHeader headingText="Profile" className="sr-only" />

        <Banners ongoingAppointment={hasOngoingAppointment} person={person} />

        <CardOrderDraftBar {...draftBar} />

        <DndContext onDragEnd={handleSectionDragEnd}>
          <SortableContext items={shownSections} strategy={verticalListSortingStrategy}>
            {shownSections.map((section) => (
              <SortableSection
                key={section.id}
                actions={sectionActions(section)}
                lazy
                section={section}
                onClick={handleSectionToggle}
                onHide={(sectionId) =>
                  setSectionToHide(shownSections.find(({ id }) => id === sectionId) ?? null)
                }>
                {sectionComponent(section.id)}
              </SortableSection>
            ))}
          </SortableContext>

          {/* Hidden cards stay out of the sortable list: they always sit at the bottom. */}
          {hiddenSections.length > 0 && (
            <SortableContext items={hiddenSections}>
              <HiddenCardsDivider
                count={hiddenSections.length}
                isOpen={showHiddenSections}
                onToggle={() => setShowHiddenSections((open) => !open)}
              />

              {showHiddenSections &&
                hiddenSections.map((section) => (
                  <SortableSection
                    key={section.id}
                    lazy
                    section={section}
                    onClick={handleSectionToggle}
                    onUnhide={unhideSection}>
                    {sectionComponent(section.id)}
                  </SortableSection>
                ))}
            </SortableContext>
          )}
        </DndContext>

        <HideCardModal
          cardTitle={sectionToHide?.title ?? null}
          editingRoleName={draftBar.editingRoleName}
          onClose={() => setSectionToHide(null)}
          onConfirm={confirmHideSection}
        />
      </Container>
    );
  }

  return null;
};

export default Profile;
